import {captureException} from "@sentry/react";
import FileDTO, {appendFileField} from "v4/DTO/FileDTO";
import {COLLECTION_TYPE, CUSTOMER_FILE_TYPE, FILE_TYPE, LIVE_COLLECTION_TYPE} from "v4/data/fieldTypes";
import {
    serializeEntryProperties
} from "v4/layouts/FormEntityLayout/components/FormEntityLayoutForm/serializeByProperties";

export function getActiveClassName(value) {
    if (!value) return null;

    let className = null;
    if (
        String(value)?.length > 0
        || value?.begin?.length > 0
        || value?.end?.length > 0
    ) {
        className = 'active';
    }
    return className;
}

const QUOTE_LINE_SERIALIZE_DEFAULTS = {vatRate: '20.00'};

export function createFormData(data, allInputs = [], canAccessProductAttributes = false) {
    const formData = new FormData();

    for (const [key, value] of Object.entries(data)) {
        if (value?.shouldBeSubmitted === false) continue;
        const fileInput = findFileInput(allInputs, key);
        if (fileInput) {
            const multiple = isCollectionType(fileInput.type);
            const dtos = FileDTO.normalize(value, {type: multiple ? fileInput.entry_type : fileInput.type});
            appendFileField(formData, key, dtos, {multiple});
            continue;
        }

        if (Array.isArray(value)) {
            if (key === "quoteLines") {
                const quoteLinesProperty = allInputs.find((input) => (input.name ?? input.key) === "quoteLines");
                if (!value.length) {
                    formData.append(`quoteLines`, '');
                } else if (quoteLinesProperty?.properties) {
                    const skipFields = canAccessProductAttributes
                        ? []
                        : ['quoteLineInfo_quoteLineInfoProductAttributes'];
                    value.forEach((line, index) =>
                        serializeEntryProperties(formData, `quoteLines[${index}]`, quoteLinesProperty.properties, line, {
                            skipFields,
                            defaults: QUOTE_LINE_SERIALIZE_DEFAULTS,
                        }));
                } else {
                    captureException(new Error("createFormData: schéma `quoteLines` introuvable dans allInputs"), {
                        extra: {allInputsCount: allInputs.length},
                    });
                    appendQuoteLinesLegacy(formData, value, canAccessProductAttributes);
                }
            } else {
                value.forEach((v, index) => {
                    if (typeof v === "object" && !Array.isArray(v)) {
                        for (let prop in v) {
                            if (v.hasOwnProperty(prop)) {
                                formData.append(`${key}[${index}][${prop}]`, v[prop]);
                            }
                        }

                        return;
                    }
                    formData.append(`${key}[]`, v)
                })
                if (value.length === 0) {
                    formData.append(key, '')
                }
            }
        } else if (value?.id) {
            formData.append(key, value.id);
        } else {
            formData.append(key, value ?? '');
        }
    }
    return formData;
}

function findFileInput(allInputs, key) {
    return allInputs.find(input => (input.name ?? input.key) === key && isFileInput(input));
}

function isFileInput({type, entry_type}) {
    if (isCollectionType(type)) return entry_type === CUSTOMER_FILE_TYPE;

    return [FILE_TYPE, CUSTOMER_FILE_TYPE].includes(type);
}

function isCollectionType(type) {
    return [COLLECTION_TYPE, LIVE_COLLECTION_TYPE].includes(type);
}

function appendQuoteLinesLegacy(formData, quoteLines, canAccessProductAttributes) {
    quoteLines.forEach((item, index) => {
        formData.append(`quoteLines[${index}][isOptional]`, item.isOptional ?? false);
        formData.append(`quoteLines[${index}][name]`, item.name ?? '');
        formData.append(`quoteLines[${index}][unitPriceExclVat]`, item.unitPriceExclVat ?? '');
        formData.append(`quoteLines[${index}][quantity]`, item.quantity ?? '');
        formData.append(`quoteLines[${index}][discountType]`, item.discountType ?? '');
        formData.append(`quoteLines[${index}][discountValue]`, item.discountValue ?? '');
        formData.append(`quoteLines[${index}][comment]`, item.comment ?? '');
        formData.append(`quoteLines[${index}][description]`, item.description ?? '');
        formData.append(`quoteLines[${index}][sectionName]`, item.sectionName ?? '');
        formData.append(`quoteLines[${index}][vatRate]`, item.vatRate ?? QUOTE_LINE_SERIALIZE_DEFAULTS.vatRate);
        formData.append(`quoteLines[${index}][quoteLineInfo][product]`, item.quoteLineInfo?.product?.id ?? '');

        if (!canAccessProductAttributes) return;

        const attributes = item.quoteLineInfo?.quoteLineInfoProductAttributes ?? [];
        if (attributes.length === 0) {
            formData.append(`quoteLines[${index}][quoteLineInfo][quoteLineInfoProductAttributes]`, '[]');
            return;
        }

        attributes.forEach((quoteLineAttribute, indexAttr) => {
            const attributePrefix = `quoteLines[${index}][quoteLineInfo][quoteLineInfoProductAttributes][${indexAttr}]`;
            formData.append(`${attributePrefix}[productAttribute]`, quoteLineAttribute.productAttribute?.id ?? '');
            formData.append(`${attributePrefix}[productAttributeLabel]`, quoteLineAttribute.productAttributeLabel ?? '');
            formData.append(`${attributePrefix}[productAttributePosition]`, quoteLineAttribute.productAttributePosition ?? '');
            formData.append(`${attributePrefix}[value]`, quoteLineAttribute.value ?? '');
        });
    });
}
